/**
 * Minimal fetch wrapper：自动带 cookie（JWT session），JSON in/out，HTTP 错误抛异常。
 * 同源部署时 base 为空，dev 用 vite proxy `/api/* → http://localhost:3000`。
 */
import type {
  ProjectSummary,
  SkuFillRun,
  ProjectDetail,
  HealthReport,
  CurrentUser,
  CurrentTenant,
  CreateSiteRequest,
  AdminStatus,
  AdminUserRow,
  GenerationStatus,
  StartGenerationResponse,
  OnboardingDraft,
  OnboardingCommitResult,
  DraftPutBody,
  FeishuTablesResponse,
  PrecheckDiffResponse,
  SiteEditRow,
  StartEditRequest,
  StartEditResponse,
  RevertEditResponse,
  UsageMonthlyResponse,
  UploadedImage,
  GenerateImageRequest,
  BlogshootStatus,
  GscStatus,
  GscBindResult,
  LaunchSiteResponse,
  DomainCandidate,
  ProductionDomainState,
  SiteMembersResponse,
  ClaudeTokenStatus,
  ClaudeTokenRefreshResult,
  InquiryMailboxRoute,
  InquiryMailboxSaveResult,
  SiteInquiryListResponse,
  SiteInquiryPatchResult,
  SiteInquiryQuoteResult,
  SiteQuoteRequestBody,
  SiteQuoteResult,
  SiteInquiryStatus,
  SiteSyncJob,
  SiteAnalytics,
  TenantRow,
  TenantUserRow,
  TenantLoginEventRow,
  TenantListResponse,
  TenantLlmUsageResponse,
  CreateTenantBody,
  PatchTenantBody,
  CreateTenantUserBody,
  PatchTenantUserBody,
} from './types.js';
import type {
  SeoOverview,
  CompetitorWatchRow,
  CwReportRow,
  CwReportDetail,
  CwSnapshot,
  AdoptionsResponse,
  ContentRunsResponse,
  DryRunResponse,
  SeedPreview,
  DiscoveryRun,
  ArticleIndexResponse,
} from './seo-types.js';

export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;
  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.status = status;
    this.body = body;
    this.name = 'ApiError';
  }
}

export interface ApiClientOptions {
  baseUrl?: string;
  fetchImpl?: typeof fetch;
}

export function createApiClient(opts: ApiClientOptions = {}) {
  const base = opts.baseUrl ?? '';
  const fetchImpl = opts.fetchImpl ?? globalThis.fetch.bind(globalThis);

  async function requestOnce<T>(path: string, init: RequestInit): Promise<T> {
    const headers = new Headers(init.headers);
    if (!headers.has('Content-Type') && init.body !== undefined && init.body !== null) {
      headers.set('Content-Type', 'application/json');
    }
    const res = await fetchImpl(base + path, {
      ...init,
      headers,
      credentials: 'include',
    });
    const text = await res.text();

    // server 重启窗口 / nginx 502 / admin static html 都不是 JSON。
    // 不要让 JSON.parse 把 SyntaxError 透传到 UI（用户看到的 "Unexpected token '<'" 一脸懵）。
    // 优先按 Content-Type 判断，再尝试 parse，失败兜底报"服务暂时不可用"。
    const ctype = res.headers.get('content-type') ?? '';
    let parsed: unknown = null;
    if (text) {
      if (ctype.includes('application/json')) {
        try {
          parsed = JSON.parse(text);
        } catch {
          throw new ApiError(res.status, '服务返回了非法 JSON（可能正在重启）', text.slice(0, 200));
        }
      } else {
        // 非 JSON 响应（HTML 错误页 / 纯文本）
        if (!res.ok) {
          throw new ApiError(
            res.status,
            res.status >= 500
              ? '服务暂时不可用（请稍后刷新）'
              : `HTTP ${res.status} (non-JSON response)`,
            text.slice(0, 200),
          );
        }
        // 2xx 但非 JSON — 不应发生（除非 path 写错打到 admin static），按错误处理
        throw new ApiError(
          res.status,
          '收到了非 JSON 响应（路径可能配置错误）',
          text.slice(0, 200),
        );
      }
    }
    if (!res.ok) {
      const msg =
        (parsed && typeof parsed === 'object' && 'error' in parsed
          ? String((parsed as { error: unknown }).error)
          : `HTTP ${res.status}`) || `HTTP ${res.status}`;
      throw new ApiError(res.status, msg, parsed);
    }
    return parsed as T;
  }

  /**
   * GET 请求遇到 5xx / 网络错误时做有限重试（覆盖 server 重启 10–30s 窗口）。
   * 非 GET 不重试，避免重复副作用。
   */
  async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const method = (init.method ?? 'GET').toUpperCase();
    const isGet = method === 'GET' || method === 'HEAD';
    const maxAttempts = isGet ? 4 : 1;
    let lastErr: unknown;
    for (let i = 0; i < maxAttempts; i++) {
      try {
        return await requestOnce<T>(path, init);
      } catch (e) {
        lastErr = e;
        if (!isGet) throw e;
        const retriable = (e instanceof ApiError && e.status >= 500) || e instanceof TypeError; // network / cors error
        if (!retriable || i === maxAttempts - 1) throw e;
        // 退避：500ms / 1.5s / 4s
        await new Promise((r) => setTimeout(r, 500 * Math.pow(3, i)));
      }
    }
    throw lastErr as Error;
  }

  return {
    me: () =>
      request<{ user: CurrentUser; tenant?: CurrentTenant | null } | { user: null; tenant?: null }>(
        '/api/me',
      ),
    /** 同一个内部账号，额外拿自营那家工厂（Strand）的 tenant：sitebox 里的消息页按它画 */
    meAsSelfOperated: () =>
      request<{ user: CurrentUser | null; tenant?: CurrentTenant | null }>(
        '/api/me?as=self-operated',
      ),
    logout: () => request<{ ok: true }>('/api/auth/logout', { method: 'POST' }),

    listSites: () => request<{ projects: ProjectSummary[] }>('/api/sites'),

    // ── 客户租户（P1；全部 admin only）──
    listTenants: (q: { status?: string; q?: string; limit?: number; skip?: number } = {}) => {
      const usp = new URLSearchParams();
      for (const [k, v] of Object.entries(q))
        if (v !== undefined && v !== '') usp.set(k, String(v));
      const qs = usp.toString();
      return request<TenantListResponse>(`/api/tenants${qs ? `?${qs}` : ''}`);
    },
    createTenant: (body: CreateTenantBody) =>
      request<{ ok: true; tenant: TenantRow }>('/api/tenants', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    getTenant: (id: string) =>
      request<{ ok: true; tenant: TenantRow; users: TenantUserRow[] }>(
        `/api/tenants/${encodeURIComponent(id)}`,
      ),
    patchTenant: (id: string, body: PatchTenantBody) =>
      request<{ ok: true; tenant: TenantRow }>(`/api/tenants/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    getTenantLlmUsage: (id: string, month?: string) =>
      request<TenantLlmUsageResponse>(
        `/api/tenants/${encodeURIComponent(id)}/llm-usage${month ? `?month=${encodeURIComponent(month)}` : ''}`,
      ),
    createTenantUser: (id: string, body: CreateTenantUserBody) =>
      request<{ ok: true; user: TenantUserRow }>(`/api/tenants/${encodeURIComponent(id)}/users`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    patchTenantUser: (id: string, uid: string, body: PatchTenantUserBody) =>
      request<{ ok: true; user: TenantUserRow }>(
        `/api/tenants/${encodeURIComponent(id)}/users/${encodeURIComponent(uid)}`,
        { method: 'PATCH', body: JSON.stringify(body) },
      ),
    createTenantLoginLink: (id: string, uid: string) =>
      request<{ ok: true; url: string; expires_at: string }>(
        `/api/tenants/${encodeURIComponent(id)}/users/${encodeURIComponent(uid)}/login-link`,
        { method: 'POST' },
      ),
    /**
     * W2：给这个账号出一张微信绑定码（10 分钟、扫一次作废）。
     * 微信没开通时后端回 503 —— 页面据此提示「还没开通」，不是报一个红错。
     */
    createTenantWechatBindQr: (id: string, uid: string) =>
      request<{
        ok: true;
        ticket: string;
        qr_url: string;
        expires_at: string;
        poll_interval_ms: number;
      }>(
        `/api/tenants/${encodeURIComponent(id)}/users/${encodeURIComponent(uid)}/wechat-bind-qrcode`,
        { method: 'POST' },
      ),
    /** 绑定码轮询。**跟工厂端登录那条不是一个端点** —— 那条会给调用方签 session */
    getTenantWechatBindQr: (id: string, uid: string, ticket: string) =>
      request<{
        ok: true;
        status: 'pending' | 'bound' | 'expired';
        wechat_openid?: string | null;
      }>(
        `/api/tenants/${encodeURIComponent(id)}/users/${encodeURIComponent(uid)}/wechat-bind-qrcode/${encodeURIComponent(ticket)}`,
      ),
    /** 工厂端「网站」（10-22）：按 Tenant.site_slugs 只给自己名下的站 */
    portalSites: () =>
      request<{ ok: true; sites: Array<{ slug: string; name: string; domain: string | null }> }>(
        '/api/portal/sites',
      ),
    portalSiteAnalytics: (slug: string, days: number) =>
      request<PortalSiteAnalytics>(
        `/api/portal/sites/${encodeURIComponent(slug)}/analytics?days=${days}`,
      ),
    portalSiteSearch: (slug: string) =>
      request<{
        ok: true;
        bound: boolean;
        days?: number;
        message?: string;
        rows: Array<{ query: string; impressions: number; clicks: number; position: number }>;
      }>(`/api/portal/sites/${encodeURIComponent(slug)}/search`),
    portalSiteGeo: (slug: string) =>
      request<{
        ok: true;
        domain: string;
        last_run: { started_at: string; cited_count: number; total: number } | null;
        questions: Array<{
          question: string;
          cited_recent: boolean[];
          matched_urls: string[];
          last_asked_at: string | null;
        }>;
      }>(`/api/portal/sites/${encodeURIComponent(slug)}/geo`),
    /** 工厂端「我的 → 成员」（10-22）：列表谁都能看，增改出码只给主账号 */
    portalMembers: () =>
      request<{
        ok: true;
        can_manage: boolean;
        wechat_qr_available: boolean;
        members: PortalMemberRow[];
      }>('/api/portal/members'),
    createPortalMember: (body: { name: string; role: 'supervisor' | 'member'; phone?: string }) =>
      request<{ ok: true; member: PortalMemberRow }>('/api/portal/members', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    patchPortalMember: (
      uid: string,
      body: { name?: string; role?: 'supervisor' | 'member'; status?: 'active' | 'disabled' },
    ) =>
      request<{ ok: true; member: PortalMemberRow }>(
        `/api/portal/members/${encodeURIComponent(uid)}`,
        { method: 'PATCH', body: JSON.stringify(body) },
      ),
    createPortalMemberBindQr: (uid: string) =>
      request<{
        ok: true;
        ticket: string;
        qr_url: string;
        expires_at: string;
        poll_interval_ms: number;
      }>(`/api/portal/members/${encodeURIComponent(uid)}/wechat-bind-qrcode`, { method: 'POST' }),
    getPortalMemberBindQr: (uid: string, ticket: string) =>
      request<{ ok: true; status: 'pending' | 'bound' | 'expired' }>(
        `/api/portal/members/${encodeURIComponent(uid)}/wechat-bind-qrcode/${encodeURIComponent(ticket)}`,
      ),
    /** 工厂端：添加微信通知接收人（与登录账号绑定是两套关系）。 */
    createWechatNotifyQr: () =>
      request<{
        ok: true;
        ticket: string;
        qr_url: string;
        expires_at: string;
        poll_interval_ms: number;
      }>('/api/wechat/notify-qrcode', { method: 'POST' }),
    getWechatNotifyQr: (ticket: string) =>
      request<{
        ok: true;
        status: 'pending' | 'bound' | 'expired';
        wechat_openid?: string | null;
      }>(`/api/wechat/notify-qrcode/${encodeURIComponent(ticket)}`),
    /**
     * AI 引用监测（P7 GEO §3.4）。**每次 run 都要花外部检索 API 的钱**，
     * 所以只有明确点了「跑一次」才调 run，页面加载只读 overview。
     */
    geoCitationStartRun: (slug: string) =>
      request<{ ok: true; run_id: string; total: number }>('/api/geo/citations/run', {
        method: 'POST',
        body: JSON.stringify({ slug }),
      }),
    geoCitationRun: (runId: string) =>
      request<{
        ok: true;
        run_id: string;
        status: 'running' | 'done' | 'failed' | 'detector_suspect';
        control_ok: boolean | null;
        total: number;
        done_count: number;
        cited_count: number;
        competitor_cited_count: number;
        error: string | null;
        log: string[];
      }>(`/api/geo/citations/runs/${encodeURIComponent(runId)}`),
    geoCitationOverview: (slug: string) =>
      request<{
        ok: true;
        slug: string;
        enabled: boolean;
        domain: string;
        used_this_month: number;
        cap: number;
        last_run: { run_id: string; status: string; started_at: string } | null;
        questions: Array<{
          source_q: string;
          ask_q: string;
          recent: boolean[];
          matched_urls: string[];
          competitor_hits: Array<{ domain: string; url: string }>;
          last_asked_at: string | null;
        }>;
      }>(`/api/geo/citations/overview?slug=${encodeURIComponent(slug)}`),
    /** 登录记录（P1 登录安全）。只读，最近 50 条 */
    listTenantLoginEvents: (id: string) =>
      request<{ ok: true; events: TenantLoginEventRow[] }>(
        `/api/tenants/${encodeURIComponent(id)}/login-events`,
      ),
    /** 上架前体检:逐 SKU 查图片/名称/价格/是否已在站(不写数据) */
    precheckSkus: (slug: string, globalSkus: string[]) =>
      request<{
        ok: true;
        slug: string;
        base_token: string;
        checks: {
          global_sku: string;
          factory_sku: string;
          title: string | null;
          sku_code: string;
          image_url: string | null;
          price_cny: number | null;
          checks: { image: boolean; title: boolean; price: boolean };
          already_on_site: boolean;
          ok: boolean;
          issues: string[];
        }[];
        summary: { total: number; ok: number; blocked: number; already_on_site: number };
        warnings: string[];
      }>(`/api/sites/${encodeURIComponent(slug)}/publish-skus/precheck`, {
        method: 'POST',
        body: JSON.stringify({ global_skus: globalSkus }),
      }),
    /** 把 CRM 供应商 SKU 批量上架到某盒子站的飞书 sku 表(后台 job,秒回 job_id,轮询进度) */
    publishSkus: (
      slug: string,
      body: {
        global_skus: string[];
        markup: number;
        publish_status?: '草稿' | '已发布';
        auto_fill_copy?: boolean;
        auto_generate?: boolean;
      },
    ) =>
      request<{ ok: true; slug: string; job_id: string; requested: number }>(
        `/api/sites/${encodeURIComponent(slug)}/publish-skus`,
        {
          method: 'POST',
          body: JSON.stringify(body),
        },
      ),
    /** 轮询上架 job 进度;job 状态在服务端内存,服务重启会 404(上架幂等,可重跑) */
    publishSkusJob: (slug: string, jobId: string) =>
      request<{
        ok: true;
        job: {
          id: string;
          status: 'running' | 'done' | 'failed';
          done: number;
          total: number;
          error?: string;
          result?: {
            requested: number;
            created: number;
            skipped_existing: number;
            skipped_no_price: number;
            images_uploaded: number;
            image_failures: number;
            created_sku_codes: string[];
            warnings: string[];
            /** 视觉富化已排队（后台 fire-and-forget，job 不等它完成） */
            enrich_queued?: boolean;
            /** 后端已触发确定性增量 sync + build（fire-and-forget） */
            site_sync?: 'building';
            /** 站还没生成过：SKU 只写进飞书表，建站时一起生成 */
            site_not_built?: boolean;
          };
          /** 看图补文案进度：上架 done 之后才跑完 */
          enrich?: {
            status: 'running' | 'done' | 'failed';
            done: number;
            total: number;
            enriched: number;
            failed: number;
            started_at?: number;
            error?: string;
          };
        };
      }>(`/api/sites/${encodeURIComponent(slug)}/publish-skus/jobs/${encodeURIComponent(jobId)}`),
    /**
     * 从飞书重新同步产品数据（回填规格字段 + 重算分类树 + 拼译文 + 重新发布）。
     * 秒回 job_id，进度靠 siteSyncJob 轮询——整条链路一分多钟，同步等会被 nginx 掐断。
     */
    startSiteSync: (slug: string) =>
      request<{ ok: true; slug: string; job_id: string }>(
        `/api/sites/${encodeURIComponent(slug)}/refresh-categories`,
        { method: 'POST', body: JSON.stringify({}) },
      ),
    siteSyncJob: (slug: string, jobId: string) =>
      request<SiteSyncJob>(
        `/api/sites/${encodeURIComponent(slug)}/refresh-categories/jobs/${encodeURIComponent(jobId)}`,
      ),
    /** 该站最近一次同步（进页面先显示上次结果，没跑过是 404） */
    latestSiteSync: (slug: string) =>
      request<SiteSyncJob>(`/api/sites/${encodeURIComponent(slug)}/refresh-categories/latest`),
    /**
     * 设 / 取消首页精选（`Project.catalog.featured_skus`）。
     *
     * 货盘模式下这是唯一的精选入口 —— 飞书 sku 表那个「是否精选」对已上站的货
     * 不同步（sync 只追加还不在站上的 SKU）。一次一条、带方向，不整份覆盖：
     * 两个人同时在后台点，整份覆盖会把对方刚加的那条冲掉且谁都不知道。
     * 满 12 条再加返 409 `featured_full`。
     */
    setCatalogFeatured: (slug: string, sku: string, featured: boolean) =>
      request<{ ok: true; featured_skus: string[]; max: number }>(
        `/api/sites/${encodeURIComponent(slug)}/catalog/featured`,
        { method: 'PUT', body: JSON.stringify({ sku, featured }) },
      ),
    getSite: (slug: string) => request<{ project: ProjectDetail }>(`/api/sites/${slug}`),
    createSite: (body: CreateSiteRequest) =>
      request<{ project: ProjectDetail }>('/api/sites', {
        method: 'POST',
        body: JSON.stringify(body),
      }),

    runPrecheck: (slug: string, level: 'l1' | 'l3rule' | 'all' = 'all') =>
      request<{ report: HealthReport; level: string }>(
        `/api/sites/${slug}/precheck?level=${level}`,
        { method: 'POST' },
      ),
    precheckStreamUrl: (slug: string) => `${base}/api/sites/${slug}/precheck/stream`,
    getReport: (slug: string) =>
      request<{
        report: HealthReport;
        latest_precheck_score: number | null;
        latest_precheck_at: string | null;
      }>(`/api/sites/${slug}/precheck/report`),
    getFeishuTables: (slug: string) =>
      request<FeishuTablesResponse>(`/api/sites/${slug}/feishu-tables`),
    loginUrl: () => `${base}/api/auth/feishu/login`,

    startGeneration: (
      slug: string,
      opts: { mode?: 'full' | 'incremental'; diffId?: string } = {},
    ) => {
      const qs =
        opts.mode === 'incremental' && opts.diffId
          ? `?mode=incremental&diff_id=${encodeURIComponent(opts.diffId)}`
          : opts.mode === 'full'
            ? `?mode=full`
            : '';
      return request<StartGenerationResponse>(`/api/sites/${slug}/generate${qs}`, {
        method: 'POST',
      });
    },
    precheckDiff: (slug: string) =>
      request<PrecheckDiffResponse>(`/api/sites/${slug}/precheck-diff`, {
        method: 'POST',
      }),
    getGenerationStatus: (slug: string) =>
      request<GenerationStatus>(`/api/sites/${slug}/generate/status`),
    generationEventsUrl: (slug: string) => `${base}/api/sites/${slug}/generate/events`,

    adminStatus: () => request<AdminStatus>('/api/admin/status'),
    adminClaudeTokenStatus: () => request<ClaudeTokenStatus>('/api/admin/claude-token/status'),
    adminClaudeTokenRefresh: () =>
      request<ClaudeTokenRefreshResult>('/api/admin/claude-token/refresh', { method: 'POST' }),
    adminListUsers: () => request<{ users: AdminUserRow[] }>('/api/admin/users'),
    /** danger：删除站点（mongo 数据；飞书 base / 域名 / Blogshoot workspace 不动）*/
    adminDeleteSite: (slug: string) =>
      request<{
        ok: true;
        slug: string;
        deleted: { project: number; site_draft: number; blog_articles: number };
        leftover: {
          feishu_base_token: string | null;
          production_domain: string | null;
          production_domain_status: string | null;
          blogshoot_workspace_id: string | null;
        };
      }>(`/api/admin/sites/${encodeURIComponent(slug)}`, {
        method: 'DELETE',
        body: JSON.stringify({ confirm_slug: slug }),
      }),
    adminGrantAdmin: (openId: string) =>
      request<{ ok: true; open_id: string; is_admin: boolean }>(
        `/api/admin/users/${openId}/grant-admin`,
        { method: 'POST' },
      ),
    adminRevokeAdmin: (openId: string) =>
      request<{ ok: true; open_id: string; is_admin: boolean; noop?: boolean }>(
        `/api/admin/users/${openId}/revoke-admin`,
        { method: 'POST' },
      ),
    /** 站点成员管理 */
    listSiteMembers: (slug: string) => request<SiteMembersResponse>(`/api/sites/${slug}/members`),
    addSiteMember: (slug: string, openId: string) =>
      request<{ ok: true; open_id: string; name: string }>(`/api/sites/${slug}/members`, {
        method: 'POST',
        body: JSON.stringify({ open_id: openId }),
      }),
    removeSiteMember: (slug: string, openId: string) =>
      request<{ ok: true; removed: string }>(
        `/api/sites/${slug}/members/${encodeURIComponent(openId)}`,
        { method: 'DELETE' },
      ),
    searchUsers: (q: string, limit = 20) =>
      request<{
        users: Array<{
          open_id: string;
          name: string;
          email: string | null;
          avatar_url: string | null;
        }>;
        degraded?: boolean;
        error?: string;
      }>(
        `/api/sites/_search-users?q=${encodeURIComponent(q)}&limit=${encodeURIComponent(String(limit))}`,
      ),

    /** 向导主营类目选项：默认 + 业务填过的，末尾是「其他」。 */
    getBrandCategories: () => request<{ categories: string[] }>(`/api/onboarding/brand-categories`),
    getOnboardingDraft: (slug: string) =>
      request<{
        draft: OnboardingDraft;
        synced?: boolean;
        feishu_updated_at?: string;
      }>(`/api/onboarding/${slug}/draft`),
    putOnboardingDraft: (slug: string, body: DraftPutBody) =>
      request<{ draft: OnboardingDraft }>(`/api/onboarding/${slug}/draft`, {
        method: 'PUT',
        body: JSON.stringify(body),
      }),
    /**
     * Stage 3：把 wizard 当前 answers 推到飞书 brand_config[0]。
     * 后端做白名单过滤 + upsert（表里没行就 batchCreate 带上 brand_slug 占位）。
     * - 200 + { ok, synced_fields, updated_at }：成功
     * - 502 SYNC_FAILED：飞书 API 错（网络 / 权限 / 字段格式）
     */
    syncOnboardingToFeishu: (slug: string, answers: Record<string, unknown>) =>
      request<{ ok: true; synced_fields: string[]; updated_at?: string }>(
        `/api/onboarding/${slug}/sync-to-feishu`,
        { method: 'POST', body: JSON.stringify({ answers }) },
      ),
    expandOnboarding: (slug: string, answers?: Record<string, unknown>) =>
      request<{ generated_json: Record<string, unknown> }>(`/api/onboarding/${slug}/llm-expand`, {
        method: 'POST',
        body: JSON.stringify(answers ? { answers } : {}),
      }),
    expandOnboardingStreamUrl: (slug: string) => `${base}/api/onboarding/${slug}/llm-expand/stream`,
    /**
     * 视觉风格 AI 撰写：基于 draft.answers 让 claude 写一段英文 style prompt。
     * 同步返回，不写库——调用方拿到后自己写到 textarea 走正常 auto-save 流程落库。
     */
    composeVisualStyle: (slug: string) =>
      request<{ visual_style_prompt: string }>(`/api/onboarding/${slug}/compose-visual-style`, {
        method: 'POST',
      }),
    aiFillBrandStreamUrl: (slug: string) => `${base}/api/onboarding/${slug}/ai-fill-brand/stream`,
    aiFillSkuStreamUrl: (slug: string) => `${base}/api/onboarding/${slug}/ai-fill-sku/stream`,
    /** 起一轮 AI 补 SKU；已经有人在跑 → started=false，run 是那一轮 */
    fillMissingPolicies: (slug: string) =>
      request<{ written: string[]; still_missing: string[] }>(
        `/api/sites/${encodeURIComponent(slug)}/precheck/fill-policies`,
        { method: 'POST' },
      ),
    skuFillStart: (slug: string) =>
      request<{ started: boolean; run: SkuFillRun }>(
        `/api/onboarding/${encodeURIComponent(slug)}/ai-fill-sku`,
        { method: 'POST' },
      ),
    skuFillLatest: (slug: string) =>
      request<{ run: SkuFillRun | null }>(
        `/api/onboarding/${encodeURIComponent(slug)}/ai-fill-sku/latest`,
      ),
    aiTranslateSkuStreamUrl: (slug: string) =>
      `${base}/api/onboarding/${slug}/ai-translate-sku/stream`,
    precheckOnboardingCommit: (slug: string) =>
      request<{ report: HealthReport }>(`/api/onboarding/${slug}/precheck-commit`, {
        method: 'POST',
      }),
    commitOnboarding: (slug: string, force = false) =>
      request<OnboardingCommitResult>(
        `/api/onboarding/${slug}/commit${force ? '?force=true' : ''}`,
        { method: 'POST' },
      ),
    extractOnboardingColor: (slug: string, logoUrl: string) =>
      request<{ primary_color: string }>(`/api/onboarding/${slug}/extract-color`, {
        method: 'POST',
        body: JSON.stringify({ logo_url: logoUrl }),
      }),
    /**
     * 测试飞书群机器人 webhook 是否配对：服务端发条 hello 文本到 url。
     * onboarding InquiryStep 的 "测试发送" 按钮调。url 不依赖 mongo（commit 前用）。
     */
    testInquiryWebhook: (slug: string, url: string) =>
      request<{ ok: boolean; error?: string }>(`/api/onboarding/${slug}/inquiry-webhook/test`, {
        method: 'POST',
        body: JSON.stringify({ url }),
      }),
    /**
     * 持久化 webhook URL 到 Project.feishu_inquiry_webhook，让公共询盘 API 立即生效。
     * InquiryStep 的"测试发送"成功后调用——不等 commit，因为 webhook 是运行时配置。
     */
    saveInquiryWebhook: (slug: string, url: string) =>
      request<{ ok: boolean }>(`/api/sites/${slug}/inquiry-webhook`, {
        method: 'PUT',
        body: JSON.stringify({ url }),
      }),

    /**
     * 自动建群：server 调飞书 im:chat:create 建一个新群，Sitebox 机器人自动加入，
     * 同时把当前登录用户拉入。成功后把 chat_id 存到 Project.feishu_inquiry_chat_id。
     * 与 webhook 字段并存：notifier 优先 chat_id，缺时 fallback webhook。
     */
    createInquiryChat: (
      slug: string,
      payload?: { name?: string; description?: string; member_open_ids?: string[] },
    ) =>
      request<{
        ok: boolean;
        chat_id?: string;
        already_exists?: boolean;
        name?: string;
        members?: string[];
        error?: string;
        message?: string;
      }>(`/api/sites/${slug}/inquiry-chat/create`, {
        method: 'POST',
        body: JSON.stringify(payload ?? {}),
      }),

    /**
     * 上传一张图片到 R2 CDN；返回带自定义域名的公网 URL。
     * 不走 request() 包装器 —— multipart 需要浏览器自动设置 Content-Type (含 boundary)。
     */
    uploadOnboardingImage: async (
      slug: string,
      file: File | Blob,
      field?: string,
    ): Promise<UploadedImage> => {
      const form = new FormData();
      form.append('file', file);
      if (field) form.append('field', field);
      const res = await fetchImpl(`${base}/api/onboarding/${slug}/upload`, {
        method: 'POST',
        body: form,
        credentials: 'include',
      });
      const text = await res.text();
      const parsed: unknown = text ? JSON.parse(text) : null;
      if (!res.ok) {
        const msg =
          (parsed && typeof parsed === 'object' && 'error' in parsed
            ? String((parsed as { error: unknown }).error)
            : `HTTP ${res.status}`) || `HTTP ${res.status}`;
        throw new ApiError(res.status, msg, parsed);
      }
      return parsed as UploadedImage;
    },

    generateOnboardingImage: (slug: string, body: GenerateImageRequest) =>
      request<UploadedImage>(`/api/onboarding/${slug}/generate-image`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),

    // ========== M3 edit-api ==========
    startEdit: (slug: string, body: StartEditRequest) =>
      request<StartEditResponse>(`/api/sites/${slug}/edit`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    editEventsUrl: (slug: string, editId: string) =>
      `${base}/api/sites/${slug}/edit/${editId}/events`,
    listEdits: (slug: string, limit = 50) =>
      request<{ edits: SiteEditRow[] }>(`/api/sites/${slug}/edits?limit=${limit}`),
    /** 拿一次 edit 完成/失败后持久化的事件流（admin 展开历史日志用） */
    getEditLog: (slug: string, editId: string) =>
      request<{ edit_id: string; status: string; events: Array<Record<string, unknown>> }>(
        `/api/sites/${slug}/edit/${encodeURIComponent(editId)}/log`,
      ),
    revertEdit: (slug: string, editId: string) =>
      request<RevertEditResponse>(`/api/sites/${slug}/edits/${editId}/revert`, {
        method: 'POST',
      }),

    getMonthlyUsage: (month?: string) =>
      request<UsageMonthlyResponse>(`/api/admin/usage/monthly${month ? `?month=${month}` : ''}`),

    getBlogshootStatus: () =>
      request<
        { connected: false } | { connected: true; masked_key: string; connected_at: string | null }
      >('/api/account/blogshoot'),
    connectBlogshoot: (api_key: string) =>
      request<{ connected: true; masked_key: string }>('/api/account/blogshoot/connect', {
        method: 'POST',
        body: JSON.stringify({ api_key }),
      }),
    disconnectBlogshoot: () =>
      request<{ connected: false }>('/api/account/blogshoot', { method: 'DELETE' }),

    getGsc: (slug: string) => request<GscStatus>(`/api/sites/${slug}/gsc`),
    bindGsc: (slug: string) =>
      request<GscBindResult>(`/api/sites/${slug}/gsc/bind`, { method: 'POST' }),

    getSiteBlogshoot: (slug: string) => request<BlogshootStatus>(`/api/sites/${slug}/blogshoot`),
    launchSite: (slug: string) =>
      request<LaunchSiteResponse>(`/api/sites/${slug}/launch`, { method: 'POST' }),
    unlaunchSite: (slug: string) =>
      request<{ ok: true }>(`/api/sites/${slug}/unlaunch`, { method: 'POST' }),

    suggestDomains: (slug: string, opts: { keyword?: string; tlds?: string[] } = {}) => {
      const body: Record<string, unknown> = {};
      if (opts.keyword) body['keyword'] = opts.keyword;
      if (opts.tlds && opts.tlds.length > 0) body['tlds'] = opts.tlds;
      return request<{ candidates: DomainCandidate[] }>(`/api/sites/${slug}/domain/suggest`, {
        method: 'POST',
        body: JSON.stringify(body),
      });
    },
    registerDomain: (slug: string, domain: string) =>
      request<{ ok: true; task_no: string; domain: string; status: string; next_step: string }>(
        `/api/sites/${slug}/domain/register`,
        { method: 'POST', body: JSON.stringify({ domain }) },
      ),
    pollDomainTask: (slug: string, task_no: string) =>
      request<
        | { ok: true; status: 'registering'; task_status: string }
        | {
            ok: true;
            status: 'ssl_pending';
            domain: string;
            instance_id: string;
            next_step: string;
          }
      >(`/api/sites/${slug}/domain/poll-task`, {
        method: 'POST',
        body: JSON.stringify({ task_no }),
      }),
    refreshDomain: (slug: string) =>
      request<{ ok: boolean }>(`/api/sites/${slug}/domain/refresh`, { method: 'POST' }),
    getDomain: (slug: string) => request<ProductionDomainState>(`/api/sites/${slug}/domain`),
    useSubdomain: (slug: string, subdomain: string) =>
      request<{
        ok: true;
        domain: string;
        status: 'ssl_pending';
        is_subdomain: true;
        next_step: string;
      }>(`/api/sites/${slug}/domain/use-subdomain`, {
        method: 'POST',
        body: JSON.stringify({ subdomain }),
      }),
    markDomainLive: (slug: string) =>
      request<{ ok: true; status: 'live' }>(`/api/sites/${slug}/domain/mark-live`, {
        method: 'POST',
      }),

    // ========== 站点询盘（业务员处理 + 买家自助查看那条链路的后台）==========
    listSiteInquiries: (
      slug: string,
      params: { status?: string; limit?: number; skip?: number } = {},
    ) => {
      const q = new URLSearchParams();
      if (params.status) q.set('status', params.status);
      if (params.limit) q.set('limit', String(params.limit));
      if (params.skip) q.set('skip', String(params.skip));
      const qs = q.toString();
      return request<SiteInquiryListResponse>(`/api/sites/${slug}/inquiries${qs ? `?${qs}` : ''}`);
    },
    updateSiteInquiry: (
      slug: string,
      id: string,
      body: {
        status?: SiteInquiryStatus;
        quote_note?: string | null;
        quote_file_url?: string | null;
      },
    ) =>
      request<SiteInquiryPatchResult>(`/api/sites/${slug}/inquiries/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    /** 站点访问分析（前台埋点看板）。days 默认 7 */
    getSiteAnalytics: (slug: string, days = 7) =>
      request<SiteAnalytics>(`/api/sites/${slug}/analytics?days=${days}`),

    resendSiteInquiryLink: (slug: string, id: string) =>
      request<{ ok: boolean; error: string | null; kind: string }>(
        `/api/sites/${slug}/inquiries/${id}/resend-link`,
        { method: 'POST' },
      ),

    /** 开报价单：保存即在 CRM 建 Quote + 生成站上报价单页链接；二开 = 新版本新链接 */
    /** 站上表单进来的询盘：一键开报价 / 改价（第二次保存 = 新版本新链接） */
    createSiteInquiryQuote: (slug: string, id: string, body: SiteQuoteRequestBody) =>
      request<SiteInquiryQuoteResult>(`/api/sites/${slug}/inquiries/${id}/quote`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),

    /**
     * WhatsApp / 邮件 / 线下进来的客户：不挂询盘开报价（2026-09-17）。
     * 身份给 customer_id；`slug` 决定报价单链接挂在哪个站的域名下。
     */
    createSiteQuote: (
      slug: string,
      body: SiteQuoteRequestBody & {
        customer_id?: string | null;
        email?: string | null;
        wa_id?: string | null;
        display_name?: string | null;
        revise_of?: string | null;
        locale?: string | null;
        draft?: boolean;
      },
    ) =>
      request<SiteQuoteResult>(`/api/sites/${slug}/quotes`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),

    listInquiryMailboxes: () =>
      request<{ routes: InquiryMailboxRoute[] }>('/api/inquiry-mailboxes'),
    createInquiryMailbox: (body: { address: string; project_slug?: string | null }) =>
      request<InquiryMailboxSaveResult>('/api/inquiry-mailboxes', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    verifyInquiryMailbox: (id: string) =>
      request<InquiryMailboxSaveResult>(`/api/inquiry-mailboxes/${id}/verify`, {
        method: 'POST',
      }),
    updateInquiryMailbox: (id: string, body: { enabled?: boolean; project_slug?: string | null }) =>
      request<{ route: InquiryMailboxRoute }>(`/api/inquiry-mailboxes/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    deleteInquiryMailbox: (id: string) =>
      request<{ ok: true }>(`/api/inquiry-mailboxes/${id}`, { method: 'DELETE' }),

    // ========== SEO 内容控制台（竞对监测 / 选题采纳 / 内容运行日志）==========
    getSeoOverview: () => request<SeoOverview>('/api/seo/overview'),
    listCompetitorWatches: () =>
      request<{
        watches: CompetitorWatchRow[];
        sites: Array<{ slug: string; site_name: string }>;
        error: string | null;
      }>('/api/seo/watches'),
    createCompetitorWatch: (body: {
      slug: string;
      domain: string;
      location_name?: string;
      language_name?: string;
    }) =>
      request<{ ok: true; watch: { id: string; domain: string } }>('/api/seo/watches', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    deleteCompetitorWatch: (id: string, slug: string) =>
      request<{ ok: true }>(`/api/seo/watches/${encodeURIComponent(id)}?slug=${slug}`, {
        method: 'DELETE',
      }),
    refreshCompetitorWatch: (id: string, slug: string) =>
      request<{ ok: true; message: string }>(`/api/seo/watches/${encodeURIComponent(id)}/refresh`, {
        method: 'POST',
        body: JSON.stringify({ slug }),
      }),
    listCompetitorSnapshots: (id: string, slug: string) =>
      request<{ snapshots: CwSnapshot[] }>(
        `/api/seo/watches/${encodeURIComponent(id)}/snapshots?slug=${slug}`,
      ),
    listCwReports: (watchId?: string) =>
      request<{ reports: CwReportRow[] }>(
        `/api/seo/reports${watchId ? `?watch_id=${encodeURIComponent(watchId)}` : ''}`,
      ),
    getCwReport: (eventId: string) =>
      request<CwReportDetail>(`/api/seo/reports/${encodeURIComponent(eventId)}`),
    listTopicAdoptions: () => request<AdoptionsResponse>('/api/seo/adoptions'),
    listContentRuns: (slug?: string) =>
      request<ContentRunsResponse>(`/api/seo/runs${slug ? `?slug=${slug}` : ''}`),
    dryRunContentStrategy: (slug: string) =>
      request<DryRunResponse>('/api/seo/dry-run', {
        method: 'POST',
        body: JSON.stringify({ slug }),
      }),
    previewSeedKeywords: (slug: string) => request<SeedPreview>(`/api/seo/seeds?slug=${slug}`),
    discoverCompetitors: (body: { slug: string; manual_seeds?: string[]; max_queries?: number }) =>
      request<DiscoveryRun>('/api/seo/discover', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    listDiscoveries: (slug?: string) =>
      request<{ discoveries: DiscoveryRun[] }>(
        `/api/seo/discoveries${slug ? `?slug=${slug}` : ''}`,
      ),

    // 文章收录（只读；催收录走 skill，不在 admin 上做动作）
    getArticleIndex: () => request<ArticleIndexResponse>('/api/seo/article-index'),
  };
}

export type ApiClient = ReturnType<typeof createApiClient>;

/** 工厂端成员一行（/api/portal/members） */
export interface PortalMemberRow {
  id: string;
  name: string;
  role: 'owner' | 'supervisor' | 'member';
  status: 'active' | 'disabled';
  wechat_bound: boolean;
  wechat_nickname: string | null;
  last_login_at: string | null;
}

/** 工厂端网站访问统计（/api/portal/sites/:slug/analytics） */
export interface PortalSiteAnalytics {
  ok: true;
  days: number;
  total_sessions: number;
  funnel: Array<{ step: string; sessions: number; rate_from_top: number }>;
  top_products: Array<{ key: string; count: number; sessions: number }>;
  top_queries: Array<{ key: string; count: number; sessions: number }>;
  no_result_queries: Array<{ key: string; count: number; sessions: number }>;
}
